import classnames from 'classnames';
import { noop } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import Modal from 'react-modal';
import { Options } from 'react-select';

import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { Document } from '../../../shared/icons';
import { LoadingDots } from '../../../shared/loading-dots/LoadingDots';
import { ConfirmationModal } from '../../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../../shared/modal/ModalHeader';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../../shared/tooltip';
import styles from './AIPlayground.module.scss';
import { MimeType } from '../../../constants/MimeType';

interface UploadMLTestDocumentsModalProps {
    isOpen: boolean;
    setUploadDocuments: (files: File[]) => void;
    modalLabel: string;
    files: File[] | null;
    isUploading: boolean;
    uploadDocument: () => void;
    closeModal: () => void;
    getDocumentNameValue: (fileName: string) => DropdownOption | null;
    documentNames: DropdownOption[] | [];
    updateDropdownValue: (dropdownValue: DropdownOption | Options<DropdownOption> | null, documentDescription: string) => void;
    disabled: boolean;
}

export const UploadMLTestDocuments: React.FC<UploadMLTestDocumentsModalProps> = ({
    isOpen,
    setUploadDocuments,
    modalLabel,
    files,
    isUploading,
    uploadDocument,
    closeModal,
    getDocumentNameValue = () => null,
    documentNames = [],
    updateDropdownValue = noop,
    disabled = false
}) => {

    const confirmDisabled = !files || disabled;

    const confirmDisabledTooltip = useMemo(() => {
        let disabledArray = !files ? ['You have not added any documents.'] : [];
        if (disabled) {
            disabledArray.push('You must select a Document Name for each document.');
        }
        return disabledArray;
    }, [files, disabled]);

    const onDrop = useCallback((acceptedFiles: File[]) => setUploadDocuments(acceptedFiles), [setUploadDocuments]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        multiple: true,
        maxFiles: 0,
        accept: {
            [MimeType.PDF]: [],
            [MimeType.DOCX]: [],
            [MimeType.MS_WORD]: [],
            [MimeType.TIFF]: []
        },
        onDrop
    });

    const testId = 'upload-ml-test-documents';

    const documentsToUpload = useMemo(() => {
        if (!files || (files && !files.length)) {
            return <div>None</div>;
        }
        return <div className={styles.selectedDocumentsWrapper}>
            <div className={styles.tableHeader}>
                <div className={styles.filenameHeaderCell}><OverflowTooltip overlayText='Filename' className={styles.headerLabel} /></div>
                <div className={styles.documentNameHeaderCell}><OverflowTooltip overlayText='Document Name' className={styles.headerLabel} /></div>
                <div className={styles.sizeHeaderCell}><OverflowTooltip overlayText='Size' className={styles.headerLabel} /></div>
            </div>
            <Scrollable maxHeight='210px'>
                {files.map(({ name, size }, index) => {
                    return (
                        <div key={index} className={classnames(styles.tableRow, { [styles.lastTableRow]: index === files.length - 1 })}>
                            <div className={styles.filenameCellWrapper}><OverflowTooltip overlayText={name} className={styles.cellLabel} /></div>
                            <div className={styles.documentNameCellWrapper}>
                                <Dropdown
                                    value={getDocumentNameValue(name)}
                                    onChange={e => updateDropdownValue(e, name)}
                                    options={documentNames}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                            <div className={styles.sizeCellWrapper}><OverflowTooltip overlayText={`${(size / 1e6).toFixed(2)}MB`} className={styles.cellLabel} /></div>
                        </div>
                    );
                })}
            </Scrollable>
        </div>;
    }, [files, updateDropdownValue, documentNames, getDocumentNameValue]);

    const documentTotal = files ? files.length : 0;
    const documentPlural = files && files.length > 1 ? 's' : '';

    const dropzoneText = useMemo(() => {
        if (isDragActive) {
            return 'Drop documents here';
        }
        if (files && files.length) {
            return `Document${documentPlural} successfully added, click here to change the document${documentPlural} to be uploaded`;
        }
        return 'Drag \'n\' drop documents here, or click to select files';
    }, [isDragActive, files, documentPlural]);

    if (isUploading) {
        return (
            <Modal
                ariaHideApp={false}
                isOpen
                className={styles.uploadingOverlay}
                style={{ overlay: { display: 'flex', zIndex: 10 } }}
            >
                <div className={styles.spinnerWrapper} data-testid={`${testId}-loading-wrapper`}>
                    <LoadingDots />
                    <div className={styles.spinnerLabel}>Uploading Document{documentPlural}</div>
                    <div className={styles.spinnerWarning}>Do not refresh or close browser during upload process</div>
                </div>
            </Modal>
        );
    }

    return (
        <ConfirmationModal
            isOpen={isOpen}
            closeModal={closeModal}
            confirmLabel='Upload'
            confirmDisabled={confirmDisabled}
            confirm={uploadDocument}
            testId={testId}
            confirmDisabledTooltip={confirmDisabledTooltip}
            closeOnOverlayClick={false}
        >
            <div className={styles.uploadDocumentWrapper} data-testid={`${testId}-wrapper`}>
                <ModalHeader
                    label={modalLabel}
                    testId={testId}
                    icon={Document}
                />
                <div {...getRootProps({ className: classnames(styles.dropzone) })}>
                    <input {...getInputProps()} />
                    <div className={styles.dropzonePlaceholder}>{dropzoneText}</div>
                </div>
                <div data-testid={`${testId}-selected-document-wrapper`} className={styles.selectedDocumentWrapper}>
                    <div className={styles.selectedDocumentHeader} data-testid={`${testId}-to-upload-label`}>{`Document${documentPlural} to be uploaded (${documentTotal})`}</div>
                    {documentsToUpload}
                </div>
            </div>
        </ConfirmationModal>
    );
};
